/**
 * Coverage Gap Analysis Algorithm
 *
 * This algorithm focuses on identifying and filling coverage gaps by strategically
 * placing mesh units. It starts with a primary router (fixed location) and iteratively
 * adds mesh units in the largest coverage gaps until target coverage levels are met
 * based on multi-frequency physics simulations.
 */
import { WIRELESS_SPECS } from '../constants';
// Import necessary physics/simulation helpers from logic.js
import {
    getMaterialPenetrationLoss,
    createCoverageGrid, // Use the one from logic.js
    simulateCoverageGridForFrequency,
    mapFrequenciesToSpeedTiers
} from '../logic';

// Constants for algorithm
const GRID_STEP = 10; // Use consistent grid step from logic.js (now represents meters)
const MAX_UNITS_CAP = 8; // Absolute maximum units to place
const TARGET_COVERAGE_PERCENT = 99; // Target coverage percentage
// Define signal strength thresholds (dBm) for coverage levels (used by mapFrequenciesToSpeedTiers)
const HIGH_SENSITIVITY_THRESHOLD = -75; // Example dBm threshold for High speed
const MEDIUM_SENSITIVITY_THRESHOLD = -80; // Example dBm threshold for Medium speed
const BASIC_SENSITIVITY_THRESHOLD = -85; // Example dBm threshold for Basic speed


// --- Helper Functions ---

const getPrimaryRouterPlacementDescription = (formData) => {
  if (formData.routerLocation === 'livingRoom') return 'Place your primary router in the living room, preferably in a central location and elevated.';
  if (formData.routerLocation === 'office') return 'Place your primary router in the home office, preferably elevated and away from other electronics.';
  if (formData.routerLocation === 'bedroom') return 'Place your primary router in the bedroom, preferably elevated and away from the bed.';
  if (formData.routerLocation === 'kitchen') return 'Place your primary router in the kitchen, but keep it away from appliances like microwaves that can cause interference.';
  if (formData.routerLocation === 'hallway') return 'Place your primary router in the hallway, which provides good central coverage for the surrounding rooms.';
  if (formData.routerLocation === 'basement') return 'Place your primary router in the basement, but be aware that this may limit coverage on upper floors.';
  if (formData.routerLocation === 'custom' && formData.customRouterLocation) return `Place your primary router in ${formData.customRouterLocation}, preferably elevated for better signal propagation.`;
  return 'Place your primary router in a central location in your home, preferably elevated for better signal propagation.';
};

// Removed getFloorPlanDimensions

// **Removed**: createObstaclesFromFormDataMeters - Obstacles now come from floorPlanLayout

// **Modified**: Get primary router location using floorPlanLayout dimensions
const getPrimaryRouterLocation = (formData, floorPlanLayout) => {
  const widthMeters = floorPlanLayout.layoutWidthMeters;
  const heightMeters = floorPlanLayout.layoutHeightMeters;
  let xMeters = widthMeters / 2;
  let yMeters = heightMeters / 2;
  if (formData.routerLocation === 'livingRoom') { xMeters *= 1.2; yMeters *= 0.8; }
  else if (formData.routerLocation === 'office') { xMeters *= 0.6; yMeters *= 0.6; }
  else if (formData.routerLocation === 'bedroom') { xMeters *= 1.4; yMeters *= 0.6; }
  else if (formData.routerLocation === 'kitchen') { xMeters *= 0.6; yMeters *= 1.4; }
  return {
      x: Math.max(0, Math.min(widthMeters, xMeters)),
      y: Math.max(0, Math.min(heightMeters, yMeters))
  };
};

// **Modified**: Find the center of the largest area with low *basic* coverage (using meter-based grid)
const findLargestCoverageGap = (grid2_4, positions) => {
    // grid2_4 contains cells with meter coordinates
    // positions contains unit positions in meters
    let maxGapScore = -1;
    // Default gap center to the middle of the area (in meters)
    let bestGapCenter = { x: grid2_4.widthMeters / 2, y: grid2_4.heightMeters / 2 };

    grid2_4.cells.forEach(cell => { // cell.x, cell.y are in meters
        const currentSignal = cell.signal; // dBm
        if (currentSignal < BASIC_SENSITIVITY_THRESHOLD) {
            let minDistToUnit = Infinity;
            // Calculate distance to existing units (meters)
            positions.forEach(p => {
                const dist = Math.sqrt(Math.pow(cell.x - p.x, 2) + Math.pow(cell.y - p.y, 2));
                minDistToUnit = Math.min(minDistToUnit, dist);
            });
            // Score favors cells far from existing units (meters) and with very low signal
            // Normalize distance score based on estimated diagonal (meters)
            const diagonal = Math.sqrt(grid2_4.widthMeters**2 + grid2_4.heightMeters**2);
            const normalizedDist = Math.min(1.0, minDistToUnit / (diagonal * 0.5)); // Normalize against half diagonal
            const gapScore = (Math.abs(currentSignal) / 100) * normalizedDist + Math.random() * 0.01; // Add noise

            if (gapScore > maxGapScore) {
                maxGapScore = gapScore;
                bestGapCenter = { x: cell.x, y: cell.y }; // Store gap center in meters
            }
        }
    });
    return bestGapCenter; // Returns coordinates in meters
};

// **Modified**: Format placements (already in meters)
const formatSolutionPlacements = (positions, formData) => {
    // Positions are already in meters
    return positions.map((pos, index) => ({
        type: pos.type,
        position: { x: pos.x, y: pos.y }, // Store meter coordinates
        description: pos.type === 'primary' ? getPrimaryRouterPlacementDescription(formData) : `Place mesh unit ${index + 1} to fill coverage gap.`,
        tips: []
    }));
};

// --- Exported Functions ---

/**
 * Runs the Coverage Gap algorithm iteratively to place a SPECIFIC number of units,
 * starting from the fixed primary router location. Uses floorPlanLayout.
 */
export const calculateCoverageGapPlacementsForCount = (formData, routerStandard, routerSpecs, unitCount, floorPlanLayout) => {
  if (unitCount < 1 || !floorPlanLayout) return []; // Check for layout
  unitCount = Math.min(unitCount, MAX_UNITS_CAP);

  // Use dimensions and obstacles from floorPlanLayout
  const widthMeters = floorPlanLayout.layoutWidthMeters;
  const heightMeters = floorPlanLayout.layoutHeightMeters;
  const obstacles = floorPlanLayout.obstacles; // Use layout obstacles

  // Determine primary location using layout dimensions
  const primaryRouterLocation = getPrimaryRouterLocation(formData, floorPlanLayout);
  const positions = [{ ...primaryRouterLocation, type: 'primary' }]; // Start with primary (meters)

  // Define 2.4GHz parameters needed for gap finding
   const params2_4 = {
       frequencyGHz: 2.4,
       transmitPowerDBm: routerSpecs.TX_POWER_2_4GHZ || 20,
       penetrationLossDB: getMaterialPenetrationLoss(2.4, formData.wallDensity, formData.constructionType)
   };

  // Iteratively add mesh units up to the desired count
  while (positions.length < unitCount) {
    // Create 2.4GHz grid using area (or layout dimensions if createCoverageGrid supports it)
    const aspectRatio = widthMeters / heightMeters || 1;
    const grid2_4 = createCoverageGrid(formData.area, aspectRatio);
    // Simulate 2.4GHz coverage using meter-based positions and layout obstacles
    simulateCoverageGridForFrequency(positions, grid2_4, obstacles, params2_4);
    // Find largest gap (returns meter coordinates)
    const gapCenter = findLargestCoverageGap(grid2_4, positions);
    // Add new unit at gap center (meter coordinates)
    positions.push({ x: gapCenter.x, y: gapCenter.y, type: 'mesh' });
  }
  return formatSolutionPlacements(positions, formData); // Format meter positions
};

/**
 * Calculates coverage percentages based on the final placements using physics simulation
 * and the provided floorPlanLayout.
 */
export const calculateCoverageGapCoveragePercentage = (unitCount, placements, formData, routerSpecs, floorPlanLayout) => {
    if (!floorPlanLayout || !placements || placements.length === 0) {
        return { basic: 0, medium: 0, high: 0 };
    }
    // Use dimensions and obstacles from floorPlanLayout
    const widthMeters = floorPlanLayout.layoutWidthMeters;
    const heightMeters = floorPlanLayout.layoutHeightMeters;
    const obstacles = floorPlanLayout.obstacles; // Meter coords

    // Extract positions (already in meters from placements)
    const positions = placements.map(p => ({...p.position, type: p.type})); // Meter coords

    // Create grids using area (or layout dimensions if createCoverageGrid supports it)
    const aspectRatio = widthMeters / heightMeters || 1;
    const grid2_4 = createCoverageGrid(formData.area, aspectRatio);
    const grid5 = createCoverageGrid(formData.area, aspectRatio);
    const grid6 = (routerSpecs.BANDS?.includes('6GHz')) ? createCoverageGrid(formData.area, aspectRatio) : null;

    // Define frequency parameters
    const params2_4 = { frequencyGHz: 2.4, transmitPowerDBm: routerSpecs.TX_POWER_2_4GHZ || 20, penetrationLossDB: getMaterialPenetrationLoss(2.4, formData.wallDensity, formData.constructionType) };
    const params5 = { frequencyGHz: 5, transmitPowerDBm: routerSpecs.TX_POWER_5GHZ || 23, penetrationLossDB: getMaterialPenetrationLoss(5, formData.wallDensity, formData.constructionType) };
    const params6 = grid6 ? { frequencyGHz: 6, transmitPowerDBm: routerSpecs.TX_POWER_6GHZ || 23, penetrationLossDB: getMaterialPenetrationLoss(6, formData.wallDensity, formData.constructionType) } : null;

    // Simulate coverage using meter-based positions and layout obstacles
    simulateCoverageGridForFrequency(positions, grid2_4, obstacles, params2_4);
    simulateCoverageGridForFrequency(positions, grid5, obstacles, params5);
    if (grid6 && params6) {
        simulateCoverageGridForFrequency(positions, grid6, obstacles, params6);
    }

    // Map frequencies using the updated grids
    return mapFrequenciesToSpeedTiers(grid2_4, grid5, grid6, routerSpecs);
};


/**
 * Main function for the Coverage Gap Algorithm.
 * Iteratively adds units to meet Basic, Medium, and High coverage targets.
 * Uses the provided floorPlanLayout.
 */
const coverageGapAlgorithm = (formData, routerStandard, routerSpecs, floorPlanLayout) => {
  // Check if floorPlanLayout is provided
  if (!floorPlanLayout) {
      console.error("Coverage Gap Algorithm requires floorPlanLayout.");
      // Return a default error structure or throw an error
      return {
          coverageLevels: { basic: { units: 0, placements: [] }, medium: { units: 0, placements: [] }, high: { units: 0, placements: [] } },
          coveragePercentages: { basic: 0, medium: 0, high: 0 }
      };
  }

  // Use dimensions and obstacles from floorPlanLayout
  const widthMeters = floorPlanLayout.layoutWidthMeters;
  const heightMeters = floorPlanLayout.layoutHeightMeters;
  const obstacles = floorPlanLayout.obstacles; // Use layout obstacles

  // Determine primary location using layout dimensions
  const primaryRouterLocation = getPrimaryRouterLocation(formData, floorPlanLayout);
  const fixedPrimaryPlacement = { ...primaryRouterLocation, type: 'primary' }; // Meter coords

  let currentUnitCount = 1; // Start with 1 (the primary)
  let currentPlacements = [fixedPrimaryPlacement]; // Start with primary (meters)
  let currentPercentages = { basic: 0, medium: 0, high: 0 };

  let basicResult = null;
  let mediumResult = null;
  let highResult = null;

  console.log(`CoverageGap: Starting iterative placement (using meters)...`);

  // Define frequency parameters once
  const params2_4 = { frequencyGHz: 2.4, transmitPowerDBm: routerSpecs.TX_POWER_2_4GHZ || 20, penetrationLossDB: getMaterialPenetrationLoss(2.4, formData.wallDensity, formData.constructionType) };
  const params5 = { frequencyGHz: 5, transmitPowerDBm: routerSpecs.TX_POWER_5GHZ || 23, penetrationLossDB: getMaterialPenetrationLoss(5, formData.wallDensity, formData.constructionType) };
  const params6 = (routerSpecs.BANDS?.includes('6GHz')) ? { frequencyGHz: 6, transmitPowerDBm: routerSpecs.TX_POWER_6GHZ || 23, penetrationLossDB: getMaterialPenetrationLoss(6, formData.wallDensity, formData.constructionType) } : null;

  // Create grids using area (or layout dimensions if createCoverageGrid supports it)
  const aspectRatio = widthMeters / heightMeters || 1;
  const grid2_4 = createCoverageGrid(formData.area, aspectRatio);
  const grid5 = createCoverageGrid(formData.area, aspectRatio);
  const grid6 = params6 ? createCoverageGrid(formData.area, aspectRatio) : null;

  while (currentUnitCount <= MAX_UNITS_CAP) {
    console.log(`CoverageGap: Calculating for ${currentUnitCount} units...`);

    // Simulate coverage using meter-based placements, obstacles, and the appropriate grid
    // Reset signals before each simulation run for the current unit count
    const currentGrid2_4 = simulateCoverageGridForFrequency([...currentPlacements], { ...grid2_4, cells: grid2_4.cells.map(c => ({...c, signal: -Infinity})) }, obstacles, params2_4);
    const currentGrid5 = simulateCoverageGridForFrequency([...currentPlacements], { ...grid5, cells: grid5.cells.map(c => ({...c, signal: -Infinity})) }, obstacles, params5);
    let currentGrid6 = null;
    if (grid6 && params6) {
        currentGrid6 = simulateCoverageGridForFrequency([...currentPlacements], { ...grid6, cells: grid6.cells.map(c => ({...c, signal: -Infinity})) }, obstacles, params6);
    }

    // Map to speed tier percentages
    currentPercentages = mapFrequenciesToSpeedTiers(currentGrid2_4, currentGrid5, currentGrid6, routerSpecs);
    console.log(`CoverageGap: ${currentUnitCount} units -> B:${currentPercentages.basic}%, M:${currentPercentages.medium}%, H:${currentPercentages.high}%`);

    // Check if targets met and store the first time they are met
    if (!basicResult && currentPercentages.basic >= TARGET_COVERAGE_PERCENT) {
      console.log(`CoverageGap: Basic target met at ${currentUnitCount} units.`);
      basicResult = { units: currentUnitCount, placements: formatSolutionPlacements([...currentPlacements], formData), coverage: {...currentPercentages} }; // Store meter placements
    }
    if (!mediumResult && currentPercentages.medium >= TARGET_COVERAGE_PERCENT) {
      console.log(`CoverageGap: Medium target met at ${currentUnitCount} units.`);
      mediumResult = { units: currentUnitCount, placements: formatSolutionPlacements([...currentPlacements], formData), coverage: {...currentPercentages} }; // Store meter placements
    }
    if (!highResult && currentPercentages.high >= TARGET_COVERAGE_PERCENT) {
      console.log(`CoverageGap: High target met at ${currentUnitCount} units.`);
      highResult = { units: currentUnitCount, placements: formatSolutionPlacements([...currentPlacements], formData), coverage: {...currentPercentages} }; // Store meter placements
    }

    // Exit loop if all targets are met OR max units reached
    if ((basicResult && mediumResult && highResult) || currentUnitCount >= MAX_UNITS_CAP) {
       console.log(`CoverageGap: Stopping iteration (Targets Met: ${!!(basicResult && mediumResult && highResult)}, Max Units: ${currentUnitCount >= MAX_UNITS_CAP})`);
       break;
    }

    // If targets not met, find the next gap and add a unit for the next iteration
    // Use the already simulated 2.4GHz grid for gap finding
    const gapCenter = findLargestCoverageGap(currentGrid2_4, currentPlacements); // Find gap (returns meter coords)
    currentPlacements.push({ x: gapCenter.x, y: gapCenter.y, type: 'mesh' }); // Add unit (meter coords)
    currentUnitCount++; // Increment count for the next loop iteration

  } // End while loop

   // If targets weren't met after reaching the cap, use the results from the max unit count achieved
   // Need to recalculate percentages for the final state if loop exited due to MAX_UNITS_CAP
   let finalPercentages = currentPercentages; // Use last calculated if targets met
   if (!(basicResult && mediumResult && highResult)) {
        console.log(`CoverageGap: Recalculating final percentages for ${currentPlacements.length} units.`);
        const finalGrid2_4 = simulateCoverageGridForFrequency([...currentPlacements], { ...grid2_4, cells: grid2_4.cells.map(c => ({...c, signal: -Infinity})) }, obstacles, params2_4);
        const finalGrid5 = simulateCoverageGridForFrequency([...currentPlacements], { ...grid5, cells: grid5.cells.map(c => ({...c, signal: -Infinity})) }, obstacles, params5);
        let finalGrid6 = null;
        if (grid6 && params6) {
            finalGrid6 = simulateCoverageGridForFrequency([...currentPlacements], { ...grid6, cells: grid6.cells.map(c => ({...c, signal: -Infinity})) }, obstacles, params6);
        }
        finalPercentages = mapFrequenciesToSpeedTiers(finalGrid2_4, finalGrid5, finalGrid6, routerSpecs);
   }
   // Use the placements from the last successful iteration
   const finalPlacementsFormatted = formatSolutionPlacements([...currentPlacements], formData);
   const finalResult = { units: currentPlacements.length, placements: finalPlacementsFormatted, coverage: {...finalPercentages} }; // Final meter placements

   // Assign final result if specific targets weren't met
   if (!basicResult) { console.log(`CoverageGap: Basic target not met, using max units (${finalResult.units}).`); basicResult = finalResult; }
   if (!mediumResult) { console.log(`CoverageGap: Medium target not met, using max units (${finalResult.units}).`); mediumResult = finalResult; }
   if (!highResult) { console.log(`CoverageGap: High target not met, using max units (${finalResult.units}).`); highResult = finalResult; }

  // Return results based on when each target was first met, or the final state if not met
  return {
    coverageLevels: {
      basic: { units: basicResult.units, placements: basicResult.placements }, // Meter placements
      medium: { units: mediumResult.units, placements: mediumResult.placements }, // Meter placements
      high: { units: highResult.units, placements: highResult.placements } // Meter placements
    },
    coveragePercentages: { // Report the coverage achieved *at the point the target was met* or the final coverage if not met
        basic: basicResult.coverage.basic,
        medium: mediumResult.coverage.medium,
        high: highResult.coverage.high
    }
  };
};

export default coverageGapAlgorithm;